import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Sprout, Calendar, CheckCircle, AlertTriangle, Package, Clock, Edit, TrendingUp, Droplets } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import QuickStats from '@/components/QuickStats';
import FarmCalendar from '@/components/FarmCalendar';
import ProductionDashboard from '@/components/ProductionDashboard';
import ActualProductionView from '@/components/ActualProductionView';
import LogHarvestCard from '@/components/LogHarvestCard';
import ForecastDashboard from '@/components/ForecastDashboard';
import TodayActionTasks from '@/components/TodayActionTasks';
import WateringScheduleManager from '@/components/WateringScheduleManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GrowingSupervisorDashboard = ({ user, onLogout }) => {
  const [todayWork, setTodayWork] = useState({ action_tasks: [], production_rooms: [] });
  const [showDateAdjustModal, setShowDateAdjustModal] = useState(null);
  const [showProductionModal, setShowProductionModal] = useState(null);
  const [showEstimateModal, setShowEstimateModal] = useState(null);
  const [todayEstimates, setTodayEstimates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [todayWorkRes, estimatesRes] = await Promise.all([
        axios.get(`${API}/supervisor/today-work`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/production-estimates/today`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTodayWork(todayWorkRes.data);
      setTodayEstimates(estimatesRes.data.estimates);
    } catch (error) {
      toast.error('Failed to fetch today\'s work');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F9FAF9]">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAF9]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>Today's Work</h1>
              <p className="text-sm text-gray-500">Growing Supervisor Dashboard - {format(new Date(), 'PPPP')}</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Welcome, {user.full_name}</span>
              <Button
                data-testid="logout-button"
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="border-gray-300 hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-800 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Action Required Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-900">{todayWork.total_actions}</div>
              <p className="text-xs text-blue-700 mt-1">Tasks needing attention</p>
            </CardContent>
          </Card>

          <Card className="border-yellow-200 bg-yellow-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-yellow-800 flex items-center gap-2">
                <Sprout className="w-4 h-4" />
                Rooms in Production
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-900">{todayWork.total_production}</div>
              <p className="text-xs text-yellow-700 mt-1">Harvesting today</p>
            </CardContent>
          </Card>

          <Card className="border-gray-100 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Date
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-bold text-gray-900">{format(new Date(), 'PPP')}</div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Panel */}
        <div className="mb-6">
          <QuickStats />
        </div>

        {/* Tabs for Today's Work, Forecast, Production, and Calendar */}
        <Tabs defaultValue="today" className="w-full">
          <TabsList className="mb-6 bg-white border border-gray-200">
            <TabsTrigger value="today" data-testid="today-work-tab">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Today's Work
            </TabsTrigger>
            <TabsTrigger value="forecast" data-testid="forecast-tab">
              <Sprout className="w-4 h-4 mr-2" />
              Forecast
            </TabsTrigger>
            <TabsTrigger value="production" data-testid="production-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Production
            </TabsTrigger>
            <TabsTrigger value="actual-production" data-testid="actual-production-tab">
              <Package className="w-4 h-4 mr-2" />
              Actual Production
            </TabsTrigger>
            <TabsTrigger value="watering" data-testid="watering-tab">
              <Droplets className="w-4 h-4 mr-2" />
              Watering
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="calendar-tab">
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
          </TabsList>

          <TabsContent value="today">
            <div className="mb-6">
              <LogHarvestCard userRole="growing_supervisor" />
            </div>
            {/* Today's Action Tasks (shared component — includes complete-with-photo modal) */}
            <TodayActionTasks showHarvest={false} compact={false} />
          </TabsContent>

          <TabsContent value="forecast">
            <ForecastDashboard userRole="growing_supervisor" />
          </TabsContent>

          <TabsContent value="production">
            <ProductionDashboard />
          </TabsContent>

          <TabsContent value="actual-production">
            <ActualProductionView userRole="growing_supervisor" currentUserId={user?.id} />
          </TabsContent>

          <TabsContent value="watering">
            <WateringScheduleManager />
          </TabsContent>

          <TabsContent value="calendar">
            <FarmCalendar roleFilter="growing_supervisor" />
          </TabsContent>
        </Tabs>

        {/* Date Adjustment Modal */}
        {showDateAdjustModal && (
          <DateAdjustmentModal
            task={showDateAdjustModal}
            onClose={() => setShowDateAdjustModal(null)}
            onSuccess={() => {
              setShowDateAdjustModal(null);
              fetchData();
            }}
          />
        )}

        {/* Production Input Modal */}
        {showProductionModal && (
          <ProductionInputModal
            room={showProductionModal}
            onClose={() => setShowProductionModal(null)}
            onSuccess={() => {
              setShowProductionModal(null);
              fetchData();
            }}
          />
        )}

        {/* Production Estimate Modal */}
        {showEstimateModal && (
          <ProductionEstimateModal
            room={showEstimateModal}
            existingEstimate={todayEstimates.find(e => e.room_id === showEstimateModal.room_id)}
            onClose={() => setShowEstimateModal(null)}
            onSuccess={() => {
              setShowEstimateModal(null);
              fetchData();
            }}
          />
        )}
      </main>
    </div>
  );
};

const DateAdjustmentModal = ({ task, onClose, onSuccess }) => {
  const [newDate, setNewDate] = useState(task.scheduled_date.split('T')[0]);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const dateFieldMap = {
    'Casing': 'casing_date',
    'Ruffling': 'ruffling_date',
    'Covering': 'covering_date',
    'Airing Start': 'airing_start_date'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/room-cycles/${task.room_cycle_id}/update-date`, {
        date_field: dateFieldMap[task.task],
        new_date: new Date(newDate).toISOString(),
        delay_reason: reason
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Date adjusted - Owner has been notified');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust date');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]" data-testid="date-adjust-modal">
        <DialogHeader>
          <DialogTitle>Adjust {task.task} Date</DialogTitle>
          <p className="text-sm text-gray-500 mt-1">{task.room_name} - {task.batch_name}</p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="current_date">Current Scheduled Date</Label>
            <Input
              id="current_date"
              value={format(new Date(task.scheduled_date), 'PPP')}
              disabled
              className="bg-gray-100"
            />
          </div>

          <div>
            <Label htmlFor="new_date">New Date</Label>
            <Input
              id="new_date"
              type="date"
              data-testid="new-date-input"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              required
            />
          </div>

          <div>
            <Label htmlFor="reason">Reason for Delay</Label>
            <Textarea
              id="reason"
              data-testid="delay-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this date is being changed..."
              rows={3}
            />
          </div>

          <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
              ⚠️ This change will be reported to the Owner as a delay.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="confirm-date-change"
              disabled={submitting}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              {submitting ? 'Saving...' : 'Confirm Change'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ProductionInputModal = ({ room, onClose, onSuccess }) => {
  const [weight, setWeight] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Log production to /api/production
      await axios.post(`${API}/production`, {
        room_id: room.room_id,
        weight_kg: parseFloat(weight),
        quality_grade: 'A',
        notes: notes || `${room.flush} - Day ${room.day_in_flush}`
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Also mark the task as complete
      await axios.post(`${API}/room-cycles/${room.room_cycle_id}/complete-task`, {
        day: room.current_day,
        task_type: room.flush,
        notes: `Harvested ${weight} kg`
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`${weight} kg harvest logged for ${room.room_name}!`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log production');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]" data-testid="production-input-modal">
        <DialogHeader>
          <DialogTitle>Log Harvest - {room.room_name}</DialogTitle>
          <div className="flex gap-2 mt-1">
            <Badge className={room.flush === 'Flush 1' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'}>
              {room.flush}
            </Badge>
            <span className="text-sm text-gray-500">{room.batch_name}</span>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="weight">Harvest Weight (kg)</Label>
            <Input
              id="weight"
              type="number"
              step="0.1"
              data-testid="harvest-weight-input"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Enter weight in kg"
              required
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Quality notes, observations..."
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-production"
              disabled={submitting || !weight}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
            >
              {submitting ? 'Logging...' : 'Log Harvest'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Production Estimate Modal
const ProductionEstimateModal = ({ room, existingEstimate, onClose, onSuccess }) => {
  const [estimate, setEstimate] = useState('');
  const [isMorning, setIsMorning] = useState(true);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Determine if it's morning or afternoon based on time
  useEffect(() => {
    const hour = new Date().getHours();
    setIsMorning(hour < 12);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/production-estimates`, {
        room_id: room.room_id,
        room_cycle_id: room.room_cycle_id,
        estimate_kg: parseFloat(estimate),
        is_morning: isMorning,
        notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Production estimate submitted for ${room.room_name}!`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit estimate');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]" data-testid="production-estimate-modal">
        <DialogHeader>
          <DialogTitle>Production Estimate - {room.room_name}</DialogTitle>
          <div className="flex gap-2 mt-1">
            <Badge className={room.flush === 'Flush 1' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'}>
              {room.flush}
            </Badge>
            <span className="text-sm text-gray-500">{room.batch_name}</span>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {existingEstimate && (
            <div className="bg-gray-50 p-3 rounded-lg border">
              <p className="text-sm text-gray-600">
                <strong>Morning Estimate:</strong> {existingEstimate.morning_estimate_kg || 0} kg
                {existingEstimate.afternoon_update_kg && (
                  <><br/><strong>Afternoon Update:</strong> {existingEstimate.afternoon_update_kg} kg</>
                )}
              </p>
            </div>
          )}

          <div>
            <Label>Estimate Type</Label>
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant={isMorning ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsMorning(true)}
                className={isMorning ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
              >
                Morning Estimate
              </Button>
              <Button
                type="button"
                variant={!isMorning ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsMorning(false)}
                className={!isMorning ? 'bg-blue-500 hover:bg-blue-600' : ''}
              >
                Afternoon Update
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="estimate">Estimated Production (kg)</Label>
            <Input
              id="estimate"
              type="number"
              step="0.1"
              data-testid="estimate-input"
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
              placeholder="Enter estimated kg"
              required
            />
          </div>

          <div>
            <Label htmlFor="est_notes">Notes (Optional)</Label>
            <Textarea
              id="est_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any observations..."
              rows={2}
            />
          </div>

          {!isMorning && existingEstimate?.morning_estimate_kg > 0 && estimate && (
            <div className={`p-3 rounded-lg border ${
              parseFloat(estimate) > existingEstimate.morning_estimate_kg 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <p className="text-sm">
                {parseFloat(estimate) > existingEstimate.morning_estimate_kg ? '📈' : '📉'} 
                {' '}Change from morning: <strong>
                  {(parseFloat(estimate) - existingEstimate.morning_estimate_kg).toFixed(1)} kg
                </strong>
                {' '}({((parseFloat(estimate) - existingEstimate.morning_estimate_kg) / existingEstimate.morning_estimate_kg * 100).toFixed(1)}%)
              </p>
              <p className="text-xs text-gray-500 mt-1">This will trigger a pop-up alert for Owner & Grower</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-estimate"
              disabled={submitting || !estimate}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {submitting ? 'Submitting...' : 'Submit Estimate'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GrowingSupervisorDashboard;
